<template>
  <PageContainer title="Sindicato">
    <div slot="header" class="header-buttons">
      <Button @handle:click="handleExport" text="Fazer download" color="primary">
        <DownloadOutline :size="18" />
      </Button>

      <Button @handle:click="handleFilter" text="Filtrar" color="primary" :fill="false">
        <FilterOutline :size="18" />
      </Button>
    </div>

    <Box space="medium" class="content">
      <h2>Pagamentos recebidos pelo sindicato</h2>

      <div class="table-container">
        <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
          @page-changed="fetchPayments" />

        <div class="loading" v-if="loading">
          <LoadingSpinner />
        </div>

        <Table v-else :headers="['Aluno(a)', 'CPF', 'Renach', 'Valor', 'Data de Pagamento', 'Autoescola']">
          <tr v-if="!payments.length">
            <td colspan="6" class="force-center">Não foi encontrado resultados na busca!</td>
          </tr>
          <tr v-else v-for="payment in payments" :key="payment.cpf">
            <td>{{ payment.nome }}</td>
            <td>{{ payment.cpf | CpfCnpjFilter }}</td>
            <td>{{ payment.renach }}</td>
            <td>{{ formatCurrency(payment.valor) }}</td>
            <td>{{ payment.pagamento }}</td>
            <td>{{ getSchema(payment.schema) }}</td>
          </tr>
        </Table>
      </div>
    </Box>

    <ExportModal ref="exportModal" type="reduced" @submit:export="submitExport" :loading="exportLoading" />

    <Modal ref="filterModal" title="Adicionar Filtros">
      <form @submit.prevent="handleSubmit" class="filter-modal">
        <div class="inputs">
          <div class="inputs-row">
            <Period legend="Data" :firstModelValue="initialDate" :secondModelValue="finalDate"
              @update:firstModelValue="$event => (this.initialDate = $event)"
              @update:secondModelValue="$event => (this.finalDate = $event)" />

            <Input mask="###.###.###-##" legend="CPF" placeholder="Digite o CPF do aluno" :modelValue="cpf"
              @update:modelValue="$event => cpf = $event" />
          </div>

          <div class="inputs-row">
            <Select class="input-full input-status" :options="schemaOptions" legend="Autoescola" placeholder="Selecione"
              :modelValue="schema" @update:modelValue="$event => (this.schema = $event)" />

            <Input class="input-full" legend="Renach" placeholder="Digite o renach do aluno" :modelValue="renach"
              @update:modelValue="$event => renach = $event" />
          </div>

          <div class="inputs-row">
            <Input class="input-full" legend="Aluno" placeholder="Digite o nome do aluno" :modelValue="aluno"
              @update:modelValue="$event => aluno = $event" />
          </div>

          <div class="buttons">
            <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger">
              <CloseCircleOutline :size="18" />
            </Button>

            <Button type="submit" text="Consultar" color="primary">
              <Magnify :size="18" />
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  </PageContainer>
</template>

<script>
import _export from '../../../services/export'

import PageContainer from 'core/PageContainer'
import Box from 'core/Box'
import Button from 'core/Button'
import Table from 'core/Table'
import Paginate from 'core/Paginate'
import LoadingSpinner from 'core/LoadingSpinner'
import Modal from 'core/Modal'
import Select from 'core/Select'
import Period from 'core/Period'
import Input from 'core/Input'
import ExportModal from '../../export/ExportModal'

import DownloadOutline from 'icons/DownloadOutline'
import FilterOutline from 'icons/FilterOutline'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'

export default {
  components: {
    PageContainer,
    Box,
    Button,
    Table,
    Paginate,
    LoadingSpinner,
    Modal,
    Select,
    Period,
    Input,
    ExportModal,

    DownloadOutline,
    FilterOutline,
    CloseCircleOutline,
    Magnify,
  },
  data() {
    return {
      schema: '',
      initialDate: '',
      finalDate: '',
      cpf: '',
      aluno: '',
      renach: '',
      payments: [],
      schemaOptions: [],
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      loading: false,
      exportLoading: false,
    }
  },
  async created() {
    await this.fetchCfcs()
    await this.fetchPayments()
  },
  methods: {
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados.map(cfc => {
            const sindicatoSuffix = cfc.sindicato ? ' - Sindicato' : ''
            return { value: cfc.tenant_schema, text: `${cfc.nome_fantasia}${sindicatoSuffix}` }
          })
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error, {
            position: 'top-right',
          })
        })
    },
    getSchema(schema) {
      if (this.schemaOptions) {
        const autoescola = this.schemaOptions.find(cfc => cfc.value === schema)

        if (autoescola) {
          return autoescola.text
        }

        return schema
      }

      return schema
    },
    async fetchPayments(page = 1) {
      this.loading = true

      const now = new Date()
      const initialDate = new Date(now.getFullYear(), now.getMonth(), 1)
      const finalDate = now
      const formattedInitialDate = this.formatDate(initialDate)
      const formattedFinalDate = this.formatDate(finalDate)

      const params = {
        page,
        periodo: [this.initialDate, this.finalDate],
        nome: this.aluno,
        cpf: this.cpf,
        autoescola: this.schema,
        renach: this.renach,
      }

      if (!this.aluno && !this.cpf && !this.schema && !this.renach && !this.initialDate && !this.finalDate) {
        params.periodo = [formattedInitialDate, formattedFinalDate]
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/faturamento/sindicato/recebimentos`, {
        params
      })
        .then(res => {
          this.payments = res.body.data.data

          this.paginationData = {
            currentPage: res.body.data.meta.current_page,
            lastPage: res.body.data.meta.last_page,
            total: res.body.data.meta.total,
          }
        })
        .catch(err => {
          this.$toasted.show(err.body.message, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async submitExport(e) {
      this.exportLoading = true

      const now = new Date()
      const initialDate = new Date(now.getFullYear(), now.getMonth(), 1)
      const finalDate = now
      const formattedInitialDate = this.formatDate(initialDate)
      const formattedFinalDate = this.formatDate(finalDate)

      const params = {
        type: e.format,
        periodo: [this.initialDate, this.finalDate],
        nome: this.aluno,
        cpf: this.cpf,
        autoescola: this.schema,
        renach: this.renach,
      }

      if (!this.aluno && !this.cpf && !this.schema && !this.renach && !this.initialDate && !this.finalDate) {
        params.periodo = [formattedInitialDate, formattedFinalDate]
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/faturamento/exportar/sindicato/recebimentos`, {
        params,
      })
        .then(res => {
          if (e.format === 'pdf') {
            _export.downloadPDF(res.body.data, 'pagamentos')
          } else {
            _export.downloadCSV(res.body.data, 'pagamentos')
          }
        })
        .catch(err => {
          this.$toasted.show(err.body.message, {
            position: 'top-right',
          })
        })

      this.exportLoading = false
    },
    async handleExport() {
      this.$refs.exportModal.open()
    },
    async handleFilter() {
      this.$refs.filterModal.open()
    },
    async handleSubmit() {
      if (!this.initialDate && !this.finalDate) {
        this.$toasted.show('Ambas as datas devem ser preenchidas.', {
          position: 'top-right',
        })

        return
      }

      if (this.initialDate && this.finalDate) {
        const initial = new Date(this.initialDate);
        const final = new Date(this.finalDate);

        if (initial > final) {
          this.$toasted.show('A data inicial deve ser menor ou igual à data final.', {
            position: 'top-right',
          })

          return
        }
      }

      this.$refs.filterModal.close()

      await this.fetchPayments()
    },
    clearInputs() {
      this.schema = ''
      this.initialDate = ''
      this.finalDate = ''
      this.cpf = ''
      this.aluno = ''
      this.renach = ''
    },
    formatCurrency(value) {
      if (!value) return 'R$ 0,00'

      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
  },
}
</script>

<style lang="scss" scoped>
.header-buttons {
  display: flex;
  gap: 0.5rem;
}

.content {
  display: flex;
  flex-direction: column;

  h2 {
    margin: 0;
    padding: 0.625rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.25rem;
    font-weight: 700;
    color: #1C2D5A;
  }

  td.force-center {
    text-align: center !important;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }

    .loading {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}

.filter-modal {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  .inputs {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .inputs-row {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;

      .input-full {
        flex: 1;
      }
    }
  }

  .buttons {
    align-self: flex-end;

    display: flex;
    gap: 0.5rem;
  }
}
</style>
